/**
 * fitRegistry — one page-wide scheduler that keeps every `.fit-scroll`
 * wrapper's `--fit-scale` in step with the width available to it.
 *
 * Why a registry and not per-component state: the Rounds page renders 50+
 * tables. Measuring them one at a time interleaves reads and writes and forces
 * a layout per table. Batching all pending wrappers through one flush costs at
 * most ~5 forced layouts for the whole page, whatever the table count, and
 * needs no React re-render (the scale is an inline custom property).
 *
 * Flush algorithm (all pending wrappers at once):
 *   A  write  --fit-scale: 1                       (measure the natural width)
 *   B  read   clientWidth, scrollWidth, base font  (skip hidden: clientWidth 0)
 *   C  write  computeScale(...)                    (proportional first guess)
 *   D  ≤3×    read scrollWidth → refineScale → write only if smaller
 *   E  read   overflow → write data-fit-scale / data-fit-overflow
 *
 * The ResizeObserver callback never writes to the DOM: it only marks wrappers
 * whose WIDTH changed and defers the flush to the next animation frame. That
 * is what keeps us clear of "ResizeObserver loop completed with undelivered
 * notifications" and of feedback loops from our own height changes.
 */
import { computeScale, minScaleFor, refineScale, MIN_FONT_PX } from './fitScale';

export interface FitOptions {
  /** Text floor in px (default 10). */
  minFontPx?: number;
}

interface Entry {
  el: HTMLElement;
  minFontPx: number;
  /** Last width reported by the ResizeObserver, for change detection. */
  roWidth: number;
}

const entries = new Map<HTMLElement, Entry>();
const pending = new Set<Entry>();

let observer: ResizeObserver | null = null;
let observerTried = false;
let windowFallback = false;
let fontsHooked = false;
let microtaskQueued = false;
let rafQueued = false;

function getObserver(): ResizeObserver | null {
  if (observerTried) return observer;
  observerTried = true;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(onResize);
  } else if (typeof window !== 'undefined' && !windowFallback) {
    // jsdom / very old WebViews: fall back to window resizes.
    windowFallback = true;
    window.addEventListener('resize', remeasureAllFits);
  }
  return observer;
}

function hookFonts() {
  if (fontsHooked) return;
  fontsHooked = true;
  // Web fonts arriving after first paint change widths — re-fit once they load.
  const fonts = typeof document !== 'undefined' ? (document as any).fonts : null;
  if (fonts && typeof fonts.ready?.then === 'function') {
    fonts.ready.then(remeasureAllFits).catch(() => { /* ignore */ });
  }
}

function onResize(list: ResizeObserverEntry[]) {
  let any = false;
  for (const r of list) {
    const e = entries.get(r.target as HTMLElement);
    if (!e) continue;
    const w = r.contentRect.width;
    if (Math.abs(w - e.roWidth) < 0.5) continue; // height-only change (e.g. our own scaling)
    e.roWidth = w;
    pending.add(e);
    any = true;
  }
  if (any) schedule('raf');
}

function schedule(when: 'microtask' | 'raf') {
  if (when === 'microtask') {
    if (microtaskQueued) return;
    microtaskQueued = true;
    Promise.resolve().then(() => { microtaskQueued = false; flushFits(); });
  } else {
    if (rafQueued) return;
    rafQueued = true;
    const raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => window.setTimeout(() => cb(Date.now()), 16);
    raf(() => { rafQueued = false; flushFits(); });
  }
}

/** Start fitting `el` (a `.fit-scroll` wrapper). Idempotent. */
export function attachFit(el: HTMLElement, opts: FitOptions = {}): void {
  if (entries.has(el)) return;
  const entry: Entry = { el, minFontPx: opts.minFontPx ?? MIN_FONT_PX, roWidth: -1 };
  entries.set(el, entry);
  getObserver()?.observe(el);
  hookFonts();
  requestFit(el, 'microtask');
}

/** Stop fitting `el`. */
export function detachFit(el: HTMLElement): void {
  const entry = entries.get(el);
  if (!entry) return;
  observer?.unobserve(el);
  entries.delete(el);
  pending.delete(entry);
}

/** Ask for a re-measure of one wrapper (e.g. after its content changed). */
export function requestFit(el: HTMLElement, when: 'microtask' | 'raf' = 'microtask'): void {
  const entry = entries.get(el);
  if (!entry) return;
  pending.add(entry);
  schedule(when);
}

/** Re-measure every wrapper on the page (fonts loaded, window resized, …). */
export function remeasureAllFits(): void {
  entries.forEach((e) => pending.add(e));
  schedule('raf');
}

/** Number of wrappers currently registered (for tests). */
export function fitCount(): number {
  return entries.size;
}

interface Measure {
  e: Entry;
  avail: number;
  natural: number;
  base: number;
  floor: number;
  scale: number;
  open: boolean;
}

/** Run the batched measure/scale pass for every pending wrapper. Exported for tests. */
export function flushFits(): void {
  if (pending.size === 0) return;
  const list = Array.from(pending).filter((e) => e.el.isConnected);
  pending.clear();
  if (list.length === 0) return;

  // A — reset to natural size (writes)
  for (const e of list) e.el.style.setProperty('--fit-scale', '1');

  // B — measure (reads)
  const measures: (Measure | null)[] = list.map((e) => {
    const avail = e.el.clientWidth;
    if (!(avail > 0)) return null; // hidden (display:none, collapsed) — leave at 1
    const table = e.el.querySelector('table') || e.el;
    const base = parseFloat(getComputedStyle(table).fontSize) || 14;
    return {
      e,
      avail,
      natural: e.el.scrollWidth,
      base,
      floor: minScaleFor(base, e.minFontPx),
      scale: 1,
      open: false,
    };
  });

  // C — first estimate (writes)
  for (const m of measures) {
    if (!m) continue;
    m.scale = computeScale(m.natural, m.avail, m.base, m.e.minFontPx);
    if (m.scale < 1) m.e.el.style.setProperty('--fit-scale', m.scale.toFixed(4));
    m.open = m.scale < 1 && m.scale > m.floor;
  }

  // D — converge: shrinking is sub-linear, so re-measure and adjust (bounded)
  for (let round = 0; round < 3; round++) {
    const open = measures.filter((m): m is Measure => !!m && m.open);
    if (open.length === 0) break;
    const widths = open.map((m) => m.e.el.scrollWidth); // reads
    let changed = false;
    open.forEach((m, i) => {
      const next = refineScale(m.scale, widths[i], m.avail, m.base, m.e.minFontPx);
      if (next < m.scale - 0.001) {
        m.scale = next;
        m.e.el.style.setProperty('--fit-scale', next.toFixed(4)); // write
        m.open = next > m.floor;
        changed = true;
      } else {
        m.open = false;
      }
    });
    if (!changed) break;
  }

  // E — report (reads, then writes)
  const overflow = measures.map((m) => (m ? m.e.el.scrollWidth > m.e.el.clientWidth + 1 : false));
  measures.forEach((m, i) => {
    if (!m) return;
    m.e.el.dataset.fitScale = m.scale.toFixed(3);
    m.e.el.dataset.fitOverflow = overflow[i] ? '1' : '0';
  });
}
